import {
  GenerateDocsDto,
  IAddCustomerBalanceEntryRequest,
  ICustomerBalanceResponse,
  ICustomerLimitedService,
  ICustomer,
  ICustomerCustomFieldValue,
  ICustomerFile,
  ICustomerMessagesPage,
  ICustomerPaymentsInformation,
  ICustomerTagsSuggestions,
  ICustomerTimeline,
  ICustomFieldDefinition,
  IReviewsResponse,
  IReviewsSummary,
  TReactions
} from '@torim-app/types';
import { adminUrlBuilder } from '@app/api/utils';

export class CustomersApi {
  static readonly path = 'customers';

  static async fetchCustomers(args?: {
    limit?: number;
    skip?: number;
    search?: string;
  }): Promise<{
    customers: Pick<ICustomer, 'id' | 'fullName' | 'tags' | 'loyalty' | 'smsOptedOut' | 'phoneNumber'>[];
    total: number;
  }> {
    const url = adminUrlBuilder(args || {}, CustomersApi.path);
    const response = await fetch(url, {
      method: 'get',
      headers: {
        'content-type': 'application/json'
      }
    });
    if (!response.ok)
      throw 'Failed to get customers';

    return await response.json();
  }

  static async fetchCustomer(customerId: string): Promise<ICustomer> {
    const url = adminUrlBuilder({}, CustomersApi.path + `/${customerId}`);
    const response = await fetch(url, {
      method: 'get',
      headers: {
        'content-type': 'application/json'
      }
    });
    if (!response.ok)
      throw 'Failed to get customer';

    return await response.json();
  }

  /**
   * Send a free-text SMS to a customer. The phone is resolved server-side from the id,
   * so this works even when the caller isn't allowed to see the number.
   */
  static async sendMessageToCustomer(
    customerId: string,
    message: string
  ): Promise<{ sent: boolean; optedOut: boolean }> {
    const url = adminUrlBuilder({}, CustomersApi.path + '/message');
    const response = await fetch(url, {
      method: 'post',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify({ customerId, message })
    });
    if (!response.ok)
      throw 'Failed to send message to customer';

    return await response.json();
  }

  static async addCustomer(
    body: Array<Partial<ICustomer>>
  ): Promise<{ clientsAdded: number }> {
    const url = adminUrlBuilder({}, CustomersApi.path);
    const response = await fetch(url, {
      method: 'post',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify(body)
    });
    if (!response.ok) {
      if (response.status === 409)
        throw 'customer-panel already exists';
      else
        throw 'Failed to create customers';
    }

    return await response.json();
  }

  static async editCustomer(
    body: Partial<Pick<ICustomer, 'fullName' | 'internalNote' | 'warningNote' | 'markerColor' | 'scheduleByPhoneOnly'>>
      /** ISO date, or null to clear. Only accepted for a loyalty-club member. */
      & { customerId: string; loyaltyBirthday?: string | null }
  ) {
    const url = adminUrlBuilder({}, CustomersApi.path + '/edit');
    const response = await fetch(url, {
      method: 'put',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify(body)
    });

    if (!response.ok)
      throw 'Failed to edit customers';

  }

  static async updateCustomerColorLabels(
    labels: Record<string, string>
  ): Promise<Record<string, string>> {
    const url = adminUrlBuilder({}, CustomersApi.path + '/color-labels');
    const response = await fetch(url, {
      method: 'put',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify({ labels })
    });
    if (!response.ok)
      throw 'Failed to update color labels';

    return await response.json();
  }

  static async createCustomFieldDefinition(
    definition: Omit<ICustomFieldDefinition, 'id'>
  ): Promise<Array<ICustomFieldDefinition>> {
    const url = adminUrlBuilder({}, `${CustomersApi.path}/custom-fields`);
    const response = await fetch(url, {
      method: 'post',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify(definition)
    });
    if (!response.ok)
      throw 'Failed to create custom field';

    return await response.json();
  }

  // Soft delete — the field is hidden for the business, the stored values are kept.
  static async hideCustomFieldDefinition(
    fieldId: string
  ): Promise<Array<ICustomFieldDefinition>> {
    const url = adminUrlBuilder({}, `${CustomersApi.path}/custom-fields/${fieldId}`);
    const response = await fetch(url, {
      method: 'delete'
    });
    if (!response.ok)
      throw 'Failed to remove custom field';

    return await response.json();
  }

  static async updateCustomFieldValue(
    body: { customerId: string; fieldId: string; value: string; date?: string; note?: string }
  ): Promise<Array<ICustomerCustomFieldValue>> {
    const url = adminUrlBuilder({}, `${CustomersApi.path}/custom-fields/value`);
    const response = await fetch(url, {
      method: 'put',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify(body)
    });
    if (!response.ok)
      throw 'Failed to update custom field value';

    return await response.json();
  }

  // Edits one existing entry (value / date / note) of a customer's field.
  static async updateCustomFieldEntry(
    body: {
      customerId: string;
      fieldId: string;
      entryId?: string;
      entryIndex?: number;
      value: string;
      date?: string;
      note?: string;
    }
  ): Promise<Array<ICustomerCustomFieldValue>> {
    const url = adminUrlBuilder({}, `${CustomersApi.path}/custom-fields/entry`);
    const response = await fetch(url, {
      method: 'put',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify(body)
    });
    if (!response.ok)
      throw 'Failed to update custom field entry';

    return await response.json();
  }

  // Removes a single value of a single customer — the field itself stays defined.
  static async removeCustomFieldEntry(
    body: { customerId: string; fieldId: string; entryId?: string; entryIndex?: number }
  ): Promise<Array<ICustomerCustomFieldValue>> {
    const url = adminUrlBuilder({}, `${CustomersApi.path}/custom-fields/entry`);
    const response = await fetch(url, {
      method: 'delete',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify(body)
    });
    if (!response.ok)
      throw 'Failed to remove custom field entry';

    return await response.json();
  }

  static async removeCustomer(
    clientId: string
  ): Promise<void> {
    const url = adminUrlBuilder({ clientId }, CustomersApi.path);
    const response = await fetch(url, {
      method: 'delete',
      headers: {
        'content-type': 'application/json'
      }
    });
    if (!response.ok)
      throw 'Failed to remove customer-panel';
  }

  /** SMS / WhatsApp messages sent to the customer, newest first; `before` = previous page's nextCursor. */
  static async fetchCustomerMessages(customerId: string, before?: string): Promise<ICustomerMessagesPage> {
    const url = adminUrlBuilder({ customerId, ...(before ? { before } : {}) }, `${CustomersApi.path}/messages`);
    const response = await fetch(url, {
      method: 'get',
      headers: {
        'content-type': 'application/json'
      }
    });
    if (!response.ok)
      throw 'Failed to get customer messages';

    return await response.json();
  }

  static async fetchCustomerHistory(
    clientId: string
  ): Promise<ICustomerTimeline[]> {
    const url = adminUrlBuilder({ clientId }, `${CustomersApi.path}/timeline`);
    const response = await fetch(url, {
      method: 'get',
      headers: {
        'content-type': 'application/json'
      }
    });
    if (!response.ok)
      throw 'Failed to get customer-panel timeline';

    return await response.json();
  }

  static async addNoteToAppointment(
    appointmentId: string,
    args: { note?: string; images?: Array<string>; }
  ): Promise<void> {
    const url = adminUrlBuilder({ apid: appointmentId }, `${CustomersApi.path}/timeline/note`);
    const response = await fetch(url, {
      method: 'put',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify(args)
    });
    if (!response.ok)
      throw `Failed to add note to appointment`;
  }

  /**
   * @param duration Optional visit length in minutes, overriding the services' own
   *   duration — the slot grid is then built around the length the owner asked for.
   */
  static async fetchSlotsByDateAndService(
    sid: string,
    date: string,
    providerId: string,
    duration?: number
  ): Promise<{ available: number[]; adminAvailable: number[] }> {
    const url = adminUrlBuilder(
      { sid, date, providerId, ...(duration ? { duration } : {}) },
      `${CustomersApi.path}/slots`
    );
    const response = await fetch(url, {
      method: 'get',
      headers: {
        'content-type': 'application/json'
      }
    });
    if (!response.ok)
      throw 'Failed to get available slots';

    return await response.json();
  }

  static async fetchTagsAndSuggestions(
    clientId?: string
  ): Promise<ICustomerTagsSuggestions> {
    const url = adminUrlBuilder(clientId ? { clientId } : {}, `${CustomersApi.path}/tags`);
    const response = await fetch(url, {
      method: 'get',
      headers: {
        'content-type': 'application/json'
      }
    });
    if (!response.ok)
      throw 'Failed to get tags';

    return await response.json();
  }

  static async addTagsToClient(
    clientId: string,
    tags: string[]
  ): Promise<void> {
    const url = adminUrlBuilder({ clientId }, `${CustomersApi.path}/tags`);
    const response = await fetch(url, {
      method: 'put',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify({ tags })
    });
    if (!response.ok)
      throw `Failed to add tags to client`;
  }

  static async fetchCustomerLimitedServices(
    clientId: string
  ): Promise<Array<string>> {
    const url = adminUrlBuilder({ clientId }, `${CustomersApi.path}/limited-service`);
    const response = await fetch(url, {
      method: 'get',
      headers: {
        'content-type': 'application/json'
      }
    });
    if (!response.ok)
      throw 'Failed to get limited services';

    return await response.json();
  }

  static async addLimitedServiceToClient(
    clientId: string,
    service: ICustomerLimitedService
  ): Promise<void> {
    const url = adminUrlBuilder({ clientId }, `${CustomersApi.path}/limited-service`);
    const response = await fetch(url, {
      method: 'put',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify(service)
    });
    if (!response.ok)
      throw `Failed to add limited services to client`;
  }

  static async removeLimitedServiceToClient(
    clientId: string,
    service: ICustomerLimitedService
  ): Promise<void> {
    const url = adminUrlBuilder({ clientId }, `${CustomersApi.path}/limited-service`);
    const response = await fetch(url, {
      method: 'delete',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify(service)
    });
    if (!response.ok)
      throw `Failed to add limited services to client`;
  }

  static async sendJoinLoyaltyProgramSms(
    clientId: string
  ): Promise<void> {
    const url = adminUrlBuilder({ clientId }, `${CustomersApi.path}/loyalty-program`);
    const response = await fetch(url, {
      method: 'post'
    });
    if (!response.ok)
      throw `Failed to add send join loyalty program sms`;
  }

  static async removeLoyaltyProgram(
    clientId: string
  ): Promise<void> {
    const url = adminUrlBuilder({ clientId }, `${CustomersApi.path}/loyalty-program`);
    const response = await fetch(url, {
      method: 'delete'
    });
    if (!response.ok)
      throw `Failed to add remove customer from loyalty program`;
  }

  static async transcribeText(formData: FormData): Promise<{ text: string; }> {
    const url = adminUrlBuilder({}, `${CustomersApi.path}/health/transcribe`);
    const response = await fetch(url, {
      body: formData,
      method: 'post'
    });

    if (!response.ok)
      throw `Failed to transcrible`;

    return response.json();
  }

  static async uploadFiles(
    formData: FormData,
    customerId: string
  ): Promise<Array<ICustomerFile>> {
    const url = adminUrlBuilder({ customerId }, `${CustomersApi.path}/health/upload-files`);
    const response = await fetch(url, {
      body: formData,
      method: 'post'
    });

    if (!response.ok)
      throw `Failed to upload files`;

    return await response.json();

  }

  static async deleteFile(
    customerId: string,
    url: string
  ): Promise<void> {
    const requestUrl = adminUrlBuilder({ customerId }, `${CustomersApi.path}/health/files`);
    const response = await fetch(requestUrl, {
      method: 'delete',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify({ url })
    });
    if (!response.ok)
      throw `Failed to delete file`;
  }

  static async generateFile(
    body: GenerateDocsDto
  ): Promise<Array<ICustomerFile>> {
    const url = adminUrlBuilder({}, `${CustomersApi.path}/health/files/generate`);
    const response = await fetch(url, {
      method: 'post',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify(body)
    });

    if (!response.ok)
      throw `Failed to generate file`;

    return await response.json();
  }

  static async fetchCustomerReviews(
    clientId: string
  ): Promise<IReviewsResponse> {
    const url = adminUrlBuilder({ clientId }, `${CustomersApi.path}/reviews`);
    const response = await fetch(url, {
      method: 'get',
      headers: {
        'content-type': 'application/json'
      }
    });
    if (!response.ok)
      throw 'Failed to get customer reviews';

    return await response.json();
  }

  static async getPaymentsInformation(
    clientId: string
  ): Promise<ICustomerPaymentsInformation> {
    const url = adminUrlBuilder({ clientId }, `${CustomersApi.path}/payment-information`);
    const response = await fetch(url, {
      method: 'get',
      headers: {
        'content-type': 'application/json'
      }
    });
    if (!response.ok)
      throw 'Failed to get payment information';

    return await response.json();
  }

  // Customer debts / credit (חובות ויתרות)
  static async getBalance(customerId: string): Promise<ICustomerBalanceResponse> {
    const url = adminUrlBuilder({ customerId }, `${CustomersApi.path}/balance`);
    const response = await fetch(url, {
      method: 'get',
      headers: {
        'content-type': 'application/json'
      }
    });
    if (!response.ok)
      throw 'Failed to get customer balance';

    return await response.json();
  }

  static async addBalanceEntry(body: IAddCustomerBalanceEntryRequest): Promise<ICustomerBalanceResponse> {
    const url = adminUrlBuilder({}, `${CustomersApi.path}/balance`);
    const response = await fetch(url, {
      method: 'post',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify(body)
    });
    if (!response.ok)
      throw 'Failed to add balance entry';

    return await response.json();
  }
}

export class ReviewsApi {
  static readonly path = 'reviews';

  static async fetchReviews(args: {
    skip?: number;
    limit?: number;
    providerId?: string,
    rank?: TReactions
  }): Promise<IReviewsResponse> {
    const url = adminUrlBuilder(args, ReviewsApi.path);
    const response = await fetch(url, {
      method: 'get',
      headers: {
        'content-type': 'application/json'
      }
    });
    if (!response.ok)
      throw 'Failed to get reviews';

    return await response.json();
  }

  static async fetchReviewsSummary(providerId?: string, customerId?: string): Promise<IReviewsSummary> {
    const url = adminUrlBuilder({ providerId, customerId }, ReviewsApi.path + '/summary');
    const response = await fetch(url, {
      method: 'get',
      headers: {
        'content-type': 'application/json'
      }
    });
    if (!response.ok)
      throw 'Failed to get reviews summary';

    return await response.json();
  }
}
